import { computed, toValue, type MaybeRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { SurveyRecurrenceFrequency } from './use-start-survey'

export const useSurveyRecurrenceSummary = (
  startDate: MaybeRef<Date>,
  recurrenceInterval: MaybeRef<number | undefined>,
  recurrenceFrequency: MaybeRef<SurveyRecurrenceFrequency | undefined>,
  short: boolean = false,
) => {
  const { locale, t } = useI18n()

  const startDateWeekday = computed(() =>
    (toValue(startDate) ?? new Date()).toLocaleDateString(locale.value, {
      weekday: 'long',
    }),
  )

  const weekOfMonth = computed(() => {
    const date = toValue(startDate) ?? new Date()
    return Math.ceil(date.getDate() / 7)
  })

  const weeklySummaryText = computed(() => {
    const interval = toValue(recurrenceInterval) ?? 1
    return short
      ? t('survey.recurrenceSummary.weeklyShort', {
          weekLabel: t('survey.recurrenceFrequency.weekLabel', { count: interval }),
          weekday: startDateWeekday.value,
        })
      : t('survey.recurrenceSummary.weekly', {
          weekLabel: t('survey.recurrenceFrequency.weekLabel', { count: interval }),
          weekday: startDateWeekday.value,
        })
  })

  const monthlySummaryText = computed(() => {
    const interval = toValue(recurrenceInterval) ?? 1
    let weekOfMonthLabel = new Intl.NumberFormat(locale.value).format(weekOfMonth.value)
    if (weekOfMonthLabel === '5') {
      weekOfMonthLabel = t('survey.recurrenceSummary.lastWeekDay')
    } else {
      weekOfMonthLabel = weekOfMonthLabel + '.'
    }
    return short
      ? t('survey.recurrenceSummary.monthlyShort', {
          monthLabel: t('survey.recurrenceFrequency.monthLabel', { count: interval }),
          weekOfMonth: weekOfMonthLabel,
          weekday: startDateWeekday.value,
        })
      : t('survey.recurrenceSummary.monthly', {
          monthLabel: t('survey.recurrenceFrequency.monthLabel', { count: interval }),
          weekOfMonth: weekOfMonthLabel,
          weekday: startDateWeekday.value,
        })
  })

  const recurrenceSummaryText = computed(() => {
    const frequency = toValue(recurrenceFrequency)
    if (frequency === SurveyRecurrenceFrequency.Week) {
      return weeklySummaryText.value
    }
    return monthlySummaryText.value
  })

  return {
    recurrenceSummaryText,
  }
}
